import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, Send, CheckCircle2, User, Mail, Phone, FileText, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { usePageTitle } from "../hooks/usePageTitle";

const REQUEST_TYPES = [
  { id: "access", label: "Confirmação e acesso aos meus dados" },
  { id: "correction", label: "Correção de dados incompletos, inexatos ou desatualizados" },
  { id: "anonymization", label: "Anonimização, bloqueio ou eliminação de dados desnecessários" },
  { id: "portability", label: "Portabilidade dos meus dados (formato eletrônico)" },
  { id: "deletion", label: "Eliminação dos dados tratados com base no consentimento" },
  { id: "share_info", label: "Informação sobre entidades com quem compartilham meus dados" },
  { id: "revoke_consent", label: "Revogação de consentimento anteriormente concedido" },
  { id: "complaint", label: "Reclamação sobre tratamento de dados" },
];

export default function MeusDados() {
  usePageTitle(
    "Meus Dados · Direitos LGPD",
    "Portal do titular de dados da RAF Direct: exercite seus direitos LGPD (Art. 18) — acesso, correção, portabilidade, eliminação e revogação de consentimento."
  );

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    cpf: "",
    request_types: [],
    message: "",
    protocol: "",
    confirm_owner: false,
  });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null);

  const update = (k) => (e) =>
    setForm({ ...form, [k]: e && e.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e });

  const toggleType = (id) => {
    setForm((f) => ({
      ...f,
      request_types: f.request_types.includes(id)
        ? f.request_types.filter((x) => x !== id)
        : [...f.request_types, id],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.request_types.length === 0) {
      toast.error("Selecione ao menos uma solicitação.");
      return;
    }
    if (!form.confirm_owner) {
      toast.error("Confirme que você é o titular ou representante legal dos dados.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/lgpd/data-request", {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        cpf: form.cpf.trim(),
        request_types: form.request_types,
        message: form.message.trim(),
        protocol: form.protocol.trim(),
      });
      setSent({ id: data.request_id, deadline_days: data.deadline_days });
      toast.success("Solicitação enviada. Responderemos em até 15 dias.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao enviar solicitação");
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-16" data-testid="lgpd-request-success">
        <div className="rounded-2xl border-2 border-emerald-300 bg-emerald-50/40 p-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-emerald-600 text-white mx-auto shadow-lg shadow-emerald-500/30">
            <CheckCircle2 className="w-8 h-8" />
          </div>
          <h1 className="mt-5 text-2xl sm:text-3xl font-bold text-[#0B2B62]">Solicitação registrada</h1>
          <p className="mt-3 text-slate-700">
            Recebemos seu pedido LGPD e enviamos para nossa equipe de proteção de dados. Você receberá uma
            resposta no e-mail informado em até <strong>{sent.deadline_days} dias corridos</strong>
            (Art. 19 da LGPD).
          </p>
          <p className="mt-3 text-xs text-slate-500">
            Protocolo interno: <span className="font-mono">{sent.id}</span>
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/" className="rounded-lg bg-[#0B2B62] text-white px-4 py-2 text-sm font-semibold">
              Voltar para o site
            </Link>
            <Link to="/politica-de-privacidade" className="rounded-lg border border-slate-300 text-slate-700 px-4 py-2 text-sm font-semibold">
              Política de Privacidade
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white" data-testid="lgpd-data-rights-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <div className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-widest text-emerald-700">
          <ShieldCheck className="h-3.5 w-3.5" /> Portal do titular · LGPD Art. 18
        </div>
        <h1 className="mt-2 text-3xl sm:text-4xl font-bold text-[#0B2B62]">Meus Dados</h1>
        <p className="mt-3 text-slate-600 leading-relaxed">
          A LGPD (Lei 13.709/2018) garante a você o direito de acessar, corrigir, portar, revogar
          consentimento ou solicitar a eliminação dos seus dados pessoais. Use este formulário para
          exercer seus direitos — nossa equipe responderá em até <strong>15 dias corridos</strong>.
        </p>

        <form onSubmit={submit} className="mt-8 space-y-6 rounded-2xl border border-slate-200 bg-white p-5 sm:p-7">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2">
              1. Sua identificação
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <label className="block">
                <span className="text-xs font-semibold text-slate-700">Nome completo *</span>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                  <input
                    required
                    value={form.name}
                    onChange={update("name")}
                    data-testid="lgpd-name"
                    className="w-full pl-9 pr-3 py-2 rounded-md border border-slate-300 text-sm focus:ring-2 focus:ring-[#0B2B62] outline-none"
                  />
                </div>
              </label>
              <label className="block">
                <span className="text-xs font-semibold text-slate-700">E-mail para resposta *</span>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                  <input
                    required
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    data-testid="lgpd-email"
                    className="w-full pl-9 pr-3 py-2 rounded-md border border-slate-300 text-sm focus:ring-2 focus:ring-[#0B2B62] outline-none"
                  />
                </div>
              </label>
              <label className="block">
                <span className="text-xs font-semibold text-slate-700">Telefone / WhatsApp</span>
                <div className="relative mt-1">
                  <Phone className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                  <input
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="(11) 99999-9999"
                    data-testid="lgpd-phone"
                    className="w-full pl-9 pr-3 py-2 rounded-md border border-slate-300 text-sm focus:ring-2 focus:ring-[#0B2B62] outline-none"
                  />
                </div>
              </label>
              <label className="block">
                <span className="text-xs font-semibold text-slate-700">CPF (opcional, ajuda a localizar)</span>
                <input
                  value={form.cpf}
                  onChange={update("cpf")}
                  data-testid="lgpd-cpf"
                  placeholder="000.000.000-00"
                  className="mt-1 w-full px-3 py-2 rounded-md border border-slate-300 text-sm focus:ring-2 focus:ring-[#0B2B62] outline-none"
                />
              </label>
              <label className="block md:col-span-2">
                <span className="text-xs font-semibold text-slate-700">
                  Protocolo do caso (se souber) — Ex.: RAF-202602-A1B2C3
                </span>
                <div className="relative mt-1">
                  <FileText className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                  <input
                    value={form.protocol}
                    onChange={update("protocol")}
                    data-testid="lgpd-protocol"
                    className="w-full pl-9 pr-3 py-2 rounded-md border border-slate-300 text-sm font-mono focus:ring-2 focus:ring-[#0B2B62] outline-none"
                  />
                </div>
              </label>
            </div>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2">
              2. Qual direito você quer exercer? *
            </div>
            <div className="grid gap-2">
              {REQUEST_TYPES.map((t) => (
                <label
                  key={t.id}
                  className={`flex items-start gap-2 p-3 rounded-lg border cursor-pointer transition-colors ${
                    form.request_types.includes(t.id)
                      ? "border-[#0B2B62] bg-blue-50"
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                  data-testid={`lgpd-type-${t.id}`}
                >
                  <input
                    type="checkbox"
                    checked={form.request_types.includes(t.id)}
                    onChange={() => toggleType(t.id)}
                    className="mt-0.5 h-4 w-4 accent-[#0B2B62]"
                  />
                  <span className="text-sm text-slate-800">{t.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2">
              3. Detalhes adicionais (opcional)
            </div>
            <textarea
              rows={4}
              value={form.message}
              onChange={update("message")}
              data-testid="lgpd-message"
              placeholder="Ex.: quero acessar minhas triagens dos últimos 6 meses; quero corrigir meu telefone; etc."
              className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm focus:ring-2 focus:ring-[#0B2B62] outline-none resize-none"
            />
          </div>

          <div className="rounded-lg border border-amber-300 bg-amber-50 p-4">
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.confirm_owner}
                onChange={update("confirm_owner")}
                data-testid="lgpd-confirm-owner"
                className="mt-1 h-4 w-4 accent-amber-700"
              />
              <span className="text-sm text-amber-900 leading-relaxed">
                Declaro que sou o titular dos dados solicitados ou seu representante legal, e que forneci
                informações verdadeiras. Estou ciente de que a RAF Direct poderá solicitar comprovantes
                adicionais para confirmar minha identidade antes de atender ao pedido.
              </span>
            </label>
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pt-2 border-t border-slate-100">
            <p className="text-xs text-slate-500 leading-relaxed">
              Encarregado (DPO):{" "}
              <a href="mailto:contato@rafdirect.com.br" className="text-[#0B2B62] underline">
                contato@rafdirect.com.br
              </a>
            </p>
            <button
              type="submit"
              disabled={busy}
              data-testid="lgpd-submit"
              className="inline-flex items-center gap-2 rounded-lg bg-[#0B2B62] hover:bg-[#0a2454] disabled:opacity-60 text-white px-5 py-2.5 text-sm font-semibold"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Enviar solicitação
            </button>
          </div>
        </form>

        <p className="mt-6 text-xs text-slate-500 text-center">
          Documentos relacionados:{" "}
          <Link to="/politica-de-privacidade" className="text-[#0B2B62] underline">
            Política de Privacidade
          </Link>{" "}
          ·{" "}
          <Link to="/termo-de-confidencialidade" className="text-[#0B2B62] underline">
            Termo de Confidencialidade
          </Link>{" "}
          ·{" "}
          <Link to="/politica-de-cookies" className="text-[#0B2B62] underline">
            Política de Cookies
          </Link>
        </p>
      </div>
    </div>
  );
}
